<template>
  <div>
    <div class="ps-4">
      <section-header v-if="!hideTitle"
                      :ressource="ressource"
                      :isExtended="extended"
                      :dialog="dialog"
                      @extended="setExtended"
                      @closeDialog="closeDialog"
                      ref="CART" />
    </div>

    <!-- LISTE TRANSACTIONS ATTENDUES (direct) -->
    <v-card class="mb-4">
      <v-card-title class="d-flex align-center">
        <span>Transactions attendues</span>
        <v-spacer />
        <span class="text-body-2">
          Sélection : <b>{{ selectedTransactionRows.length }}</b>
        </span>
      </v-card-title>

      <v-data-table :headers="headersTransactions"
                    :items="transactions"
                    item-value="id"
                    show-select
                    :return-object="false"
                    v-model="selectedTransactionRows"
                    :items-per-page="-1"
                    :height="320"
                    fixed-header
                    hide-default-footer
                    density="compact"
                    no-data-text="Aucune transaction attendue">
        <template v-slot:[`item.type`]="{ item }">
          {{ labelType(item.type) }}
        </template>
        <template v-slot:[`item.amount`]="{ item }">
          {{ formatMoney(item.amount) }}
        </template>
        <template v-slot:[`item.dateTransaction`]="{ item }">
          {{ formatDate(item.dateTransaction) }}
        </template>
      </v-data-table>
    </v-card>

    <!-- LISTE ECRITURES BANCAIRES -->
    <v-card>
      <v-card-title class="d-flex align-center">
        <span>Écritures bancaires ({{displayCount}})</span>
        <v-spacer />
        <span class="text-body-2">
          Sélection : <b>{{ selectedBankRows.length }}</b>
        </span>
      </v-card-title>

      <v-card-text class="pb-0 d-flex ga-2 align-center flex-wrap">
        <span>Montant</span>
        <v-text-field v-model="montantSearch"
                      :placeholder="montantSearch ? '' : 'Recherche +/- 10 €'"
                      density="compact"
                      hide-details
                      style="min-width: 170px" />
        <span>Dates</span>
        <v-text-field v-model="dateSearch"
                      :placeholder="dateSearch ? '' : 'ex : 2025-08'"
                      density="compact"
                      hide-details
                      style="min-width: 170px" />
        <span>Label</span>
        <v-text-field v-model="labelSearch"
                      :placeholder="labelSearch ? '' : 'ex GPOD & VIR'"
                      density="compact"
                      hide-details
                      style="min-width: 170px" />
        <v-checkbox v-model="pointed"
                    :label="pointedLabel"
                    dense
                    hide-details>
        </v-checkbox>
        <v-btn :loading="bankLoading"
               :disabled="!canSearch"
               @click="loadBankMatching">
          Rechercher
        </v-btn>
      </v-card-text>

      <v-data-table :headers="headersBank"
                    :items="bankLogsAll"
                    item-value="id"
                    show-select
                    :return-object="false"
                    v-model="selectedBankRows"
                    :items-per-page="-1"
                    :height="320"
                    fixed-header
                    hide-default-footer
                    density="compact"
                    no-data-text="Aucune écriture">
        <template v-slot:[`item.dateReleve`]="{ item }">
          {{ formatDate(item.dateReleve) }}
        </template>
        <template v-slot:[`item.dateOperation`]="{ item }">
          {{ formatDate(item.dateOperation) }}
        </template>
        <template v-slot:[`item.dateValeur`]="{ item }">
          {{ formatDate(item.dateValeur) }}
        </template>
        <template v-slot:[`item.montant`]="{ item }">
          {{ formatMoney(item.montant) }}
        </template>
      </v-data-table>
    </v-card>

    <!-- BANDEAU RAPPROCHEMENT -->
    <v-card class="mt-3">
      <v-card-title>Rapprochement</v-card-title>
      <v-card-text class="d-flex align-center ga-4 flex-wrap">
        <div class="text-body-2">
          Total transactions : <b>{{ formatMoney(sumTx) }}</b>
          ({{ selectedTransactionRows.length }})
        </div>
        <div class="text-body-2">
          Total banque : <b>{{ formatMoney(sumBank) }}</b>
          ({{ selectedBankRows.length }})
        </div>
        <div class="text-body-2">
          Écart : <b>{{ formatMoney(diff) }}</b>
        </div>
        <v-spacer />
        <etablissement-select v-if="creditEta"
                              :showMissing="false"
                              v-model:value=etablissementId
                              :moduleId=1
                              libelle="'Etablissement crédité'"
                              newLabel=''
                              selectText=""
                              :autoSelect=true
                              :ressource=ressourceEtablissementSelect
                              :required="true"
                              :readOnly="false"
                              :sectionName="sectionName">
        </etablissement-select>
        <loader-button v-if="!production&!creditEta"
                       class="ms-2"
                       @callback="simuBank"
                       label='Simulation banque'
                       :loading="loadingSimu"
                       :disabled="!canSimuBank">
        </loader-button>
        <loader-button class="ms-2"
                       @callback="link"
                       :loading="loadingLink"
                       :label="actionLabel"
                       :disabled="!canLinkGroup">
        </loader-button>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
  import { defineComponent, type PropType } from "vue";
  import SectionHeader from '@/framework/base/components/SectionHeader.vue';
  import { Sections } from '@/generated/classes/AppSchema';
  import { GenSection, RessourceGenSection } from '@/generated/ressources/generatedRessources';
  import { DateFormat } from "@/framework/base/helpers/DateFormat";
  import LoaderButton from '@/framework/base/components/LoaderButton.vue';
  import BankLog, { BankQuery } from '@/generated/classes/BankLog';
  import Transaction, { TransactionTypes, TransactionBank } from '@/generated/classes/Transaction';
  import { storeSession } from '@/generated/stores';
  import HubServices from '@/generated/hub/HubServices';
  import { isProduction } from '@/framework/base/helpers/Environment';
  import EtablissementSelect from '@/extend/controls/etablissement/EtablissementSelect';
  import { AppControls, RessourceAppControl, } from '@/ressources/globalRessources';

  export default defineComponent({
    name: "BankReconciliationNm",
    components: {
      SectionHeader,
      LoaderButton,
      EtablissementSelect
    },
    props: {
      hideTitle: { type: Boolean },
      extended: { type: Boolean },
      dialog: { type: Boolean },
      transaction: { type: Object as PropType<Transaction>, required: true }
    },
    data() {
      return {
        sectionName: Sections.BANK_DETAIL,

        // Transactions attendues
        transactionsLoading: false,
        transactions: [] as Transaction[],
        selectedTransactionRows: [] as number[],

        // Banque
        bankLog: new BankLog(),
        bankLoading: false,
        bankLogsAll: [] as BankLog[],
        selectedBankRows: [] as number[],

        // Recherche banque (serveur)
        montantSearch: "",
        lastMontantSearch: "",
        dateSearch: "",
        pointed: false,
        lastDateSearch: "",
        labelSearch: "",
        lastLabelSearch: "",
        lastPointed: false,
        logCount: 0,

        // Action
        loadingLink: false,
        loadingSimu: false,

        headersTransactions: [
          { title: "Date de demande", key: 'dateTransaction', sortable: true },
          { title: "Type", key: 'type', sortable: true },
          { title: "Emetteur", key: 'ownerLabel', sortable: true },
          { title: "Référence", key: 'reference', sortable: true },
          { title: "Montant", key: 'amount', align: 'end', sortable: true },
        ],
        headersBank: [
          { title: 'Date relévé', key: 'dateReleve', sortable: true },
          { title: 'Montant', key: 'montant', sortable: true, align: 'end' },
          { title: 'Clé de banque', key: 'bankId', sortable: true },
          { title: 'Pièce', key: 'piece', sortable: true },
          { title: 'Ligne', key: 'ligne', sortable: true },
          { title: 'Label', key: 'label', sortable: true }
        ],

        mPeriod: new Date(),

        etablissementId: 0
      };
    },

    computed: {
      actionLabel() {
        if (this.creditEta) {
          return 'Affecter';
        } else {
          return 'Rapprocher';
        }
      },
      ressource() {
        return RessourceGenSection(GenSection.RAPPROCHEMENT);
      },
      ressourceEtablissementSelect() {
        return RessourceAppControl(AppControls.CREDITED_ETAB);
      },
      sumTx(): number {
        const ids = new Set<number>(this.selectedTransactionRows);
        return this.transactions
          .filter(t => ids.has(t.id))
          .reduce((acc, t) => acc + Number(t.amount ?? 0), 0);
      },
      sumBank(): number {
        const ids = new Set<number>(this.selectedBankRows);
        return this.bankLogsAll
          .filter(b => ids.has((b as any).id))
          .reduce((acc, b) => acc + Number((b as any).montant ?? 0), 0);
      },
      diff(): number {
        return Math.round((this.sumBank + this.sumTx) * 100) / 100;
      },
      creditEta(): boolean {
        let avoir = this.selectedBankRows.length > 0 && this.selectedTransactionRows.length == 0;
        this.selectedBankRows.forEach((id) => {
          const bl = this.bankLogsAll.find(a => a.id == id);;
          avoir = avoir && bl != null && bl.montant < 0 && bl.rapprochementId == 0
        });
        return avoir
      },
      canLinkGroup(): boolean {
        return (this.selectedTransactionRows.length > 0 && this.selectedBankRows.length > 0 && !this.loadingSimu) ||
          this.creditEta && this.etablissementId > 0;
      },
      canSimuBank(): boolean {
        return this.selectedTransactionRows.length > 0 && this.selectedBankRows.length == 0 && !this.loadingLink;
      },
      canSearch(): boolean {
        return this.montantSearch != this.lastMontantSearch ||
          this.dateSearch != this.lastDateSearch ||
          this.labelSearch != this.lastLabelSearch ||
          this.lastPointed != this.pointed;
      },
      displayCount(): string {
        let res = this.logCount.toString();
        if (this.logCount > 100) {
          res += ' dont 100 affichées, merci de filtrer'
        }
        return res;
      },
      pointedLabel(): string {
        if (this.pointed) {
          return 'Toutes';
        } else {
          return 'Non pointées';
        }
      },
      production(): boolean {
        return isProduction();
      }
    },

    watch: {
      // Quand la sélection de transactions change → on recharge les bank logs côté serveur
      selectedTransactionRows: {
        deep: true,
        handler() {
          this.montantSearch = this.sumTx.toFixed(2);
          if (this.selectedTransactionRows.length == 1) {
            const selected = this.transactions.find(a => a.id === this.selectedTransactionRows[0]);
            if (selected && selected.type == TransactionTypes.VIREMENT_RECU) {
              this.dateSearch = '';
              this.labelSearch = '';
              this.pointed = false;
            }
            this.loadBankMatching();
          }
        }
      }
    },

    async created() {
      HubServices.connection.invoke('EnterTransaction', storeSession.httpToken);
      HubServices.onTransactionRapproched(this.loadWaitedTransactions);
      await this.loadWaitedTransactions();
    },

    methods: {
      loadWaitedTransactions() {
        Transaction.waitedList(this.transaction).then((res) => {
          this.loadingLink = false;
          this.bankLogsAll = [];
          this.selectedBankRows = [];
          this.selectedTransactionRows = [];
          const waited = res.find(a => a.id === this.transaction.id);
          if (waited) {
            this.montantSearch = this.transaction.amount.toString();
            this.selectedTransactionRows = Array.from(
              new Set([...this.selectedTransactionRows, waited.id])
            );
          }
          this.transactions = res;
        });
      },

      loadBankMatching() {
        this.bankLoading = true;
        this.lastMontantSearch = this.montantSearch;
        this.lastDateSearch = this.dateSearch;
        this.lastLabelSearch = this.labelSearch;
        this.lastPointed = this.pointed;

        const bankQuery = new BankQuery();
        bankQuery.montant = Number(this.lastMontantSearch);
        bankQuery.count = this.selectedTransactionRows.length;
        const first = this.transactions.find(a => a.id === this.selectedTransactionRows[0]);
        if (first && first.type == TransactionTypes.VIREMENT_RECU) {
          bankQuery.after = first.dateTransaction;
          bankQuery.reference = first.reference;
        }
        bankQuery.date = this.lastDateSearch;
        bankQuery.label = this.lastLabelSearch;
        bankQuery.notPointed = !this.pointed;

        BankLog.LoadMatching(bankQuery)
          .then((res) => {
            this.bankLogsAll = res.bankLogs ?? [];
            this.logCount = res.count;
            this.selectedBankRows = [];
            if (this.bankLogsAll.length == 1) {
              this.selectedBankRows.push(this.bankLogsAll[0].id);
            }
          })
          .finally(() => {
            this.bankLoading = false;
          });
      },

      async link() {
        if (!this.canLinkGroup) return;

        this.loadingLink = true;
        const payload: TransactionBank = {
          transactionIds: [...this.selectedTransactionRows],
          bankLogIds: [...this.selectedBankRows],
          etablissementId: this.etablissementId
        };
        Transaction.link(payload).then((res) => {
          if (res) {
            this.loadWaitedTransactions();
          }
          this.loadBankMatching();
          this.loadingLink = false;
        }).catch(() => {
          this.loadingLink = false;
        })
      },

      async simuBank() {
        if (!this.canSimuBank) return;

        this.loadingSimu = true;
        const transactions: Transaction[] = [];
        this.selectedTransactionRows.forEach((trId) => {
          const tr = this.transactions.find(a => a.id == trId);
          if (tr) {
            transactions.push(tr);
          }
        });
        Transaction.simuBank(transactions).then((res) => {
          if (res) {
            this.loadWaitedTransactions();
            this.loadingSimu = false;
          }
        }).catch(() => {
          this.loadingSimu = false;
        })
      },
      labelType(value: any): string {
        const type = value as TransactionTypes;
        if (!value) return '';
        if (type == TransactionTypes.CARTE_BANCAIRE_RECU) {
          return 'Carte bancaire';
        } else if (type == TransactionTypes.VIREMENT_RECU) {
          return 'Virement';
        } else if (type == TransactionTypes.PRELEVEMENT) {
          return 'Prélèvement';
        }
        return '';
      },
      formatDate(value: any): string {
        if (!value) return '';
        const iso = (value instanceof Date) ? value.toISOString() : String(value);
        return DateFormat.toEdit(iso);
      },
      formatMoney(value: any): string {
        const n = Number(value ?? 0);
        return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
      },

      setExtended(extended: any): void {
        this.$emit('extended', extended);
      },
      closeDialog() {
        this.$emit('closeDialog');
      }
    }
  });
</script>
